import { Grid } from '@mui/material';
import { useEffect, useState } from 'react';
import DataTable from 'react-data-table-component';
import { Dropmenu, Search } from '../../Components';
import { Link, useNavigate } from 'react-router-dom';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { API_URL } from '../../Config';
import Loading from '../../Components/Loading';
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner';
import { useGetEstimationQuery } from '../../Redux/Service/EstimateApi';
import { getAuthToken } from '../../Redux/Service/FetchBaseQuery';
import axios from 'axios';
import StyledDashboardLayout from '../style';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { toast } from 'react-toastify';
import { useModuleHasPermissions } from '../../hooks/useModulePermissions';

function EstimateList() {
   const MySwal = withReactContent(Swal);
   const [loading, setLoading] = useState(true);
   const navigate = useNavigate();
   const [estimationData, setEstimationData] = useState(null);
   const [searchQuery, setSearchQuery] = useState('');
   const [page, setPage] = useState(1);
   const [rowsPerPage, setRowsPerPage] = useState(10);
   const hasPermissions = useModuleHasPermissions('estimate');
   const {
      data: estimation,
      refetch,
      isLoading,
      isError,
      error,
   } = useGetEstimationQuery({
      limit: rowsPerPage,
      page: page - 1,
      orderBy: 'DESC',
      search: searchQuery,
   });

   const customStyles = {
      rows: {
         style: {
            margin: '0px',
            padding: 0,
         },
      },
      headCells: {
         style: {
            margin: '0px',
            padding: '10px 15px',
            fontWeight: 600,
            fontSize: 15,
            background: '#3F30FF1A',
         },
      },
      cells: {
         style: {
            margin: '0px',
            padding: '10px 15px',
            fontSize: 15,
            fontWeight: 400,
            color: '#726E6E',
         },
      },
   };

   useEffect(() => {
      refetch();
   }, [refetch, searchQuery]);

   useEffect(() => {
      if (!isLoading && !isError) {
         setEstimationData(estimation?.response);
         setLoading(false);
      }
   }, [isLoading, isError, estimation]);

   const handleEdit = (id) => {
      navigate(`/estimation-edit/${id}`);
   };

   const handleView = (id) => {
      navigate(`/estimation/${id}`);
   };

   const handleSearch = (search) => {
      setSearchQuery(search);
      setPage(1);
   };

   const handleDelete = async (id) => {
      const result = await MySwal.fire({
         title: 'Are you sure?',
         text: "You won't be able to revert this!",
         icon: 'warning',
         showCancelButton: true,
         confirmButtonColor: '#3085d6',
         cancelButtonColor: '#d33',
         confirmButtonText: 'Yes, delete it!',
      });
      if (!result.isConfirmed) return;
      try {
         const response = await axios.post(
            `${API_URL}estimate/delete/${id}`,
            {},
            {
               headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${getAuthToken()}`,
               },
            },
         );

         if (response.status === 200 && response.data.statusCode === 200) {
            await MySwal.fire(
               'Deleted!',
               response.data.message || 'Estimate has been deleted successfully.',
               'success',
            );
            refetch();
         } else if (response.data.statusCode === 400) {
            await MySwal.fire(
               'Cannot Delete!',
               response.data.message || 'This estimate is referenced in another table or module.',
               'error',
            );
         } else if (response.data.statusCode === 404) {
            await MySwal.fire(
               'Not Found!',
               response.data.message || 'Estimate not found.',
               'error',
            );
         } else {
            await MySwal.fire(
               'Error!',
               response.data.message || 'There was an error deleting the estimate.',
               'error',
            );
         }
      } catch (error) {
         console.error('Error deleting estimate:', error);
         const errorMessage =
            error.response?.data?.message || 'Failed to delete estimate. Please try again.';
         await MySwal.fire('Error!', errorMessage, 'error');
      }
   };

   const columns = [
      {
         name: 'SL No',
         cell: (row, index) => index + 1,
         button: false,
      },
      {
         name: 'Name',
         selector: (row) => row.name && row.name,
         sortable: true,
         wrap: true,
         cell: (row) => (
            <div style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
               {row.name && row.name}
            </div>
         ),
      },
      {
         name: 'Opportunity',
         selector: (row) => row.opportunity && row?.opportunity?.name,
         sortable: true,
         wrap: true,
         cell: (row) => (
            <div style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
               {row.opportunity && row?.opportunity?.name}
            </div>
         ),
      },
      {
         name: 'Created By',
         selector: (row) => row.user && row.user?.name,
         sortable: true,
         wrap: true,
         cell: (row) => (
            <div style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
               {row.user && row.user?.name}
            </div>
         ),
      },
      {
         name: 'Date',
         selector: (row) => new Date(row.createdAt).toDateString(),
         sortable: true,
      },
      {
         name: 'Total Hours',
         selector: (row) => row.totalHours ?? 0,
         sortable: true,
      },
      {
         name: 'Total Cost',
         selector: (row) => row.totalCost ?? 0,
         sortable: true,
         cell: (row) => (
            <div>
               {row?.currency?.symbol} {row?.totalCost}{' '}
            </div>
         ),
      },
      {
         name: 'FILE',
         selector: (row) => (
            <div>
               <Link className="dark-link" to={API_URL + row.file} target="_blank" download>
                  <CloudDownloadIcon />
               </Link>
            </div>
         ),
      },
      // {
      //     name: 'Status',
      //     selector: row => row.status ?? 'Active'
      // },
      {
         name: 'Action',
         cell: (row) => (
            <div>
               <Dropmenu
                  className="dark-link"
                  handleEdit={() => handleEdit(row.id)}
                  handleView={() => handleView(row.id)}
                  handleDelete={() => handleDelete(row.id)}
                  canEdit={hasPermissions?.canUpdate}
                  canDelete={hasPermissions?.canDelete}
               />
            </div>
         ),
         button: false,
      },
   ];

   if (loading) {
      return <Loading />;
   }

   return (
      <StyledDashboardLayout>
         <div className="content-wrapper">
            <div className="content-header">
               <div className="container-fluid">
                  <div className="d-flex justify-content-between mb-2">
                     <div className="pageTitle">
                        <h1 className="m-0">Estimation</h1>
                     </div>
                     {hasPermissions?.canAdd && (
                        <div className="btnWrap">
                           <Link to="/estimate-add" className="btn addBtn">
                              <DocumentScannerIcon /> Add Estimation
                           </Link>
                        </div>
                     )}
                  </div>
               </div>
            </div>
            <div className="card m-3 customCard">
               {isLoading ? (
                  <div>Loading...</div>
               ) : isError ? (
                  <div>Error: {error?.message}</div>
               ) : (
                  <div>
                     <Grid container spacing={2}>
                        <Grid item md={6} xs={12}></Grid>
                        <Grid item md={6} xs={12} display="flex" justifyContent="flex-end">
                           <Search onSearch={(query) => handleSearch(query)} />
                        </Grid>
                     </Grid>

                     <DataTable
                        columns={columns}
                        data={estimationData.data}
                        pagination
                        paginationServer
                        paginationTotalRows={estimationData.pagination.total}
                        paginationDefaultPage={page}
                        paginationPerPage={rowsPerPage}
                        onChangePage={setPage}
                        onChangeRowsPerPage={(newPerPage) => {
                           setRowsPerPage(newPerPage);
                           setPage(1);
                        }}
                        customStyles={customStyles}
                        className="dataTable"
                     />
                  </div>
               )}
            </div>
         </div>
      </StyledDashboardLayout>
   );
}

export default EstimateList;
